import { Button, Flex, Select, Tag, Typography } from 'antd'
import { FC, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { MatchTier, TermMatchStatus } from './valueListFilterMatching'

const { Text } = Typography

export interface TermMatchRowProps {
  term: string
  status: TermMatchStatus
  tier?: MatchTier
  candidateValues: ReadonlyArray<string>
  chosenValues: ReadonlyArray<string>
  secondaryValuesByValue: Readonly<Record<string, string>>
  shouldUppercaseValues: boolean
  duplicateOfLine?: number
  onChooseValues: (term: string, values: ReadonlyArray<string>) => void
}

const TermText = styled(Text)`
  && {
    flex: 0 0 12rem;
    word-break: break-word;
  }
`

const CandidateSelectContainer = styled.div`
  flex: 1;
  min-width: 12rem;

  .ant-select {
    width: 100%;
  }
`

export const TermMatchRow: FC<TermMatchRowProps> = ({
  term,
  status,
  tier,
  candidateValues,
  chosenValues,
  secondaryValuesByValue,
  shouldUppercaseValues,
  duplicateOfLine,
  onChooseValues,
}) => {
  // Only the label changes — `value` stays raw, so the filter is unaffected.
  const candidateOptions = useMemo(
    () =>
      candidateValues.map((value) => {
        const shownValue = shouldUppercaseValues ? value.toUpperCase() : value
        const secondary = secondaryValuesByValue[value]
        return {
          value,
          label: secondary ? `${shownValue} (${secondary})` : shownValue,
        }
      }),
    [candidateValues, secondaryValuesByValue, shouldUppercaseValues]
  )

  const selectedValues = useMemo(() => [...chosenValues], [chosenValues])

  const handleChange = useCallback(
    (values: string[]) => onChooseValues(term, values),
    [onChooseValues, term]
  )

  const handleChooseAll = useCallback(
    () => onChooseValues(term, candidateValues),
    [candidateValues, onChooseValues, term]
  )

  if (status !== 'ambiguous') {
    return (
      <Flex align='center' gap='small'>
        <TermText>{term}</TermText>
        {status === 'duplicate' ? (
          <Tag color='gold'>
            {duplicateOfLine
              ? `Duplicate of line ${duplicateOfLine}`
              : 'Duplicate'}
          </Tag>
        ) : (
          <Tag>Not found</Tag>
        )}
      </Flex>
    )
  }

  return (
    <Flex align='center' gap='small'>
      <TermText>{term}</TermText>
      <Tag color='warning'>
        {candidateValues.length} {tier} matches
      </Tag>
      <CandidateSelectContainer>
        <Select
          mode='multiple'
          options={candidateOptions}
          optionFilterProp='label'
          value={selectedValues}
          onChange={handleChange}
          placeholder='Choose which values to include'
        />
      </CandidateSelectContainer>
      <Button size='small' type='link' onClick={handleChooseAll}>
        All
      </Button>
    </Flex>
  )
}
